"use client";

import { useEffect, useRef, useState } from "react";
import { Pause, Play, Terminal } from "lucide-react";
import { TwinMark } from "@product/components/app/twin/twin-mark";
import { AssistantMark, ExternalChat } from "./external-chat";
import { CustomerAnswer } from "./customer-answer";
import { NORTHSTAR_ANSWER, type StoryAnswer } from "./answer-content";
import styles from "./connected-answer.module.css";

const DOORS = [
  { id: "twin", name: "Twin chat", connection: "Shared context" },
  { id: "chatgpt", name: "ChatGPT", connection: "Via MCP" },
  { id: "claude", name: "Claude", connection: "Via MCP" },
  { id: "copilot", name: "Copilot", connection: "Agent via MCP" },
  { id: "terminal", name: "Terminal", connection: "Twin CLI" },
] as const;
type Door = (typeof DOORS)[number];

function DoorIcon({ door }: { door: Door }) {
  if (door.id === "twin") return <TwinMark />;
  if (door.id === "terminal") return <Terminal size={18} aria-hidden="true" />;
  return <AssistantMark provider={door.id} />;
}

function TerminalAnswer({ answer }: { answer: StoryAnswer }) {
  return <div className={styles.terminal}>
    <div className={styles.terminalDots} aria-hidden="true"><i /><i /><i /><span>twin, business context</span></div>
    <pre><code><span className={styles.shellPrompt}>$ </span>{`twin query --question ${JSON.stringify(answer.question)}`}</code></pre>
    <p className={styles.terminalLabel}>ANSWER</p>
    <p>{answer.parts.map(part => part.text).join("")}</p>
    <div className={styles.terminalRecords}><span>SOURCES</span><p>{answer.records.map((record, index) => <span key={record.name}>0{index + 1}  {record.system.split(" / ")[0]} / {record.title}<br /></span>)}</p></div>
    <p className={styles.terminalNote}>The same context, from your terminal.</p>
    <span className={styles.shellPrompt} aria-hidden="true">$ ▍</span>
  </div>;
}

/** How long each client stays in front before the stack shuffles. */
const SHUFFLE_MS = 2000;

/** One answer across five illustrative clients; only the front card is interactive. */
export function ConnectedAnswer({ active, answer = NORTHSTAR_ANSWER }: { active: boolean; answer?: StoryAnswer }) {
  const root = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [reduced, setReduced] = useState(true);
  const [keyboard, setKeyboard] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setReduced(media.matches);
    updateMotion();
    media.addEventListener("change", updateMotion);
    let intersecting = false;
    const updateVisibility = () => setVisible(intersecting && !document.hidden);
    const observer = new IntersectionObserver(([entry]) => { intersecting = !!entry?.isIntersecting; updateVisibility(); });
    if (root.current) observer.observe(root.current);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => { observer.disconnect(); media.removeEventListener("change", updateMotion); document.removeEventListener("visibilitychange", updateVisibility); };
  }, []);

  useEffect(() => {
    if (!active || !visible || !playing || focused || reduced) return;
    const timer = window.setTimeout(() => { setKeyboard(false); setSelected(index => (index + 1) % DOORS.length); }, SHUFFLE_MS);
    return () => window.clearTimeout(timer);
  }, [active, visible, playing, focused, reduced, selected]);

  return <div ref={root} className={styles.exhibit} data-keyboard={keyboard}>
    <div className={styles.stack}
      onFocusCapture={() => setFocused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      {DOORS.map((door, index) => <article key={door.id} className={styles.card} data-door={door.id} data-depth={(index - selected + DOORS.length) % DOORS.length} aria-hidden={selected !== index} inert={selected !== index} aria-label={`${door.name} illustrative interface`}>
        <header><DoorIcon door={door} /><strong>{door.name}</strong><span>{door.connection}</span></header>
        <div className={styles.cardContent}>
          {door.id === "twin" ? <CustomerAnswer compact answer={answer} /> : door.id === "terminal" ? <TerminalAnswer answer={answer} /> : <ExternalChat provider={door.id} answer={answer} />}
        </div>
      </article>)}
    </div>
    <div className={styles.controls} aria-label="Choose how to use the Twin">
      {DOORS.map((door, index) => <button key={door.id} data-door={door.id} type="button" aria-label={`Show ${door.name}`} aria-pressed={selected === index} onClick={event => { setKeyboard(event.detail === 0); setSelected(index); }}><DoorIcon door={door} /><span>{door.name}</span></button>)}
      {!reduced && <button type="button" className={styles.play} aria-label={playing ? "Pause card shuffle" : "Resume card shuffle"} onClick={() => setPlaying(value => !value)}>{playing ? <Pause size={13} aria-hidden="true" /> : <Play size={13} aria-hidden="true" />}</button>}
    </div>
    <p className={styles.caption}>One Twin. Your tools. The same business knowledge.<span>Example interfaces · External tools connect through MCP with your permission</span></p>
  </div>;
}
